import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import LovCommercialPlans from "src/components/Lov/CommercialPlans";
import formatDate from "src/utils/formatDate";
import DateTimePicker from "src/components/DateTimePicker";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { addPlanSchema, revalidatePlanSchema } from "./schemas";
import { IModalCommercialPlan, IRefModalCommercialPlan } from "./interface";

const ModalChangeCommercialPlan: React.ForwardRefRenderFunction<IRefModalCommercialPlan, IModalCommercialPlan> = ({
	efa001Id
}, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();

	// Modal add change contract item
	const modalChangeCommercialPlan = useRef<RCCRefModalProps>(null);
	const {
		reset,
		handleSubmit,
		register,
		control,
		clearErrors,
		formState: { errors },
		setError
	} = useForm();
	const onCallbackFunction = useRef<Function>(() => {});
	const [isRevalidate, setIsRevalidate] = useState(false);
	const revalidateDate = useRef<Date>(new Date());
	const actionButtonChange = async (onCallback: Function) => {
		reset();
		setIsRevalidate(false);
		onCallbackFunction.current = onCallback;
		modalChangeCommercialPlan.current?.openModal();
	};
	const onSubmit = async (data: any) => {
		try{
			clearErrors();
			modalChangeCommercialPlan.current?.setLoadingButton();

			if(isRevalidate)
				await revalidatePlanSchema(translate).validate({ ...data },{
					abortEarly: false
				});
			else
				await addPlanSchema(translate).validate({ ...data },{
					abortEarly: false
				});

			const response = await RCCServicesApi.putRequest({
				method: "BusinessGroup/ChangeCommercialPlan",
				body: {
					efd001Id: Number(data.efd001Id),
					efa012Dtvalini: formatDate(data.efa012Dtvalini),
					efa001Id,
					isRevalidate,
					revalidateDate: revalidateDate.current
				}
			});

			addToast({
				color: "green",
				description: translate("Plano comercial alterado com sucesso"),
				title: translate("Sucesso")
			});

			if(isRevalidate)
				window.setTimeout(() => addToast({
					color: "blue",
					description: `${translate("Plano comercial foi revalidado e será o vigente a partir da data")} ${formatDate(data.efa012Dtvalini, currentLanguage)}`,
					title: translate("Atenção")
				}), 1000);

			modalChangeCommercialPlan.current?.resetLoadingButton();
			modalChangeCommercialPlan.current?.closeModal();
			onCallbackFunction.current(response.data);
		}catch(err){
			modalChangeCommercialPlan.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, translate("Erro ao alterar plano comercial"), addToast);
		}
	};
	const [efd001InputId, setEfd001InpuId] = useState(0);
	const actionButtonRevalidate = async (efd001Id: number, startDate: Date, onCallback: Function) => {
		reset();
		setIsRevalidate(true);
		setEfd001InpuId(efd001Id);
		revalidateDate.current = startDate;
		onCallbackFunction.current = onCallback;
		modalChangeCommercialPlan.current?.openModal();
	};

	const actionButtonDelete = (id: number, name: string, onCallback: Function) => {
		confirm(
			translate("Excluir plano comercial"),
			`${translate("Deseja realmente excluir o plano comercial {{name}} do grupo empresa", { name })}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "BusinessGroup/DeleteCommercialPlan",
					urlParameters: {
						id
					}
				});
	
				addToast({
					color: "green",
					description: translate("Plano comercial excluído com sucesso"),
					title: translate("Sucesso")
				});

				onCallback();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir plano comercial"), addToast);
			}
		);
	};

	useImperativeHandle(ref, () => ({
		actionButtonChange,
		actionButtonRevalidate,
		actionButtonDelete
	}));
    
	return (
		<Modal
			ref={modalChangeCommercialPlan}
			header={{
				title: isRevalidate ? translate("Revalidar plano comercial") : translate("Troca de plano comercial"),
				icon: { name: "refresh" }
			}}
			onSubmit={handleSubmit(onSubmit)}
			withoutForm
			styleInline={`${ModalSide} min-height: 478px; max-height: 478px;`}
		>
			<RCCGrid container item direction="column" alignItems="center">
				{!isRevalidate ? (
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<LovCommercialPlans 
							name="efd001Id"
							label={translate("Plano comercial")}
							control={control}
							required
							error={errors.efd001Id}
						/>
					</RCCGrid>
				) : (
					<input 
						type="text"
						value={efd001InputId}
						{...register("efd001Id")} 
						style={{ display: "none" }} 
						readOnly
					/>
				)}
				<RCCGrid
					container
					item
					xs={12}
					sm={10}
				>
					<DateTimePicker
						name="efa012Dtvalini"
						type="datePicker"
						label={translate("Data inicial de validade")}
						control={control}
						required
						error={errors.efa012Dtvalini}
						defaultValue={new Date()}
					/>
				</RCCGrid>
			</RCCGrid>
		</Modal>
	);
};

export default forwardRef(ModalChangeCommercialPlan);
